import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Textarea } from "./ui/field";
import { cn } from "./ui/cn";
import type { PolicySummary } from "@/lib/types";

/** Same grammar as the API: `@policy.<policy-id>`; an @ inside an e-mail or a word is plain text. */
const POLICY_MENTION = /(?<![\p{L}\p{N}_.@-])@policy\.([a-z0-9]+(?:-[a-z0-9]+)*)(?![\p{L}\p{N}_-])/gu;
export const MENTION_PREFIX = "@policy.";

export function findPolicyMentions(value: string) {
  return [...value.matchAll(POLICY_MENTION)].map((match) => ({
    start: match.index ?? 0,
    end: (match.index ?? 0) + match[0].length,
    policyId: match[1]!,
  }));
}

type Query = { start: number; end: number; query: string };

function queryAt(value: string, cursor: number): Query | null {
  const before = value.slice(0, cursor);
  const start = before.lastIndexOf("@");
  if (start < 0 || (start > 0 && /[\p{L}\p{N}_.@-]/u.test(before[start - 1]!))) return null;
  const typed = before.slice(start + 1);
  if (!/^(p(o(l(i(c(y(\.[a-z0-9-]*)?)?)?)?)?)?)?$/.test(typed) && !/^[a-z0-9-]*$/.test(typed)) return null;
  const suffix = value.slice(cursor).match(/^[a-z0-9.-]*/)?.[0].length ?? 0;
  return { start, end: cursor + suffix, query: typed.startsWith("policy.") ? typed.slice(7) : typed };
}

export function PolicyMentionTextarea({
  id, value, onChange, policies, className, label, status = "ready",
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  policies: PolicySummary[];
  className?: string;
  label: string;
  /** Where the live policy list is; @ always answers, even when there is nothing to pick. */
  status?: "loading" | "error" | "ready";
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLTextAreaElement>(null);
  const pendingCursor = useRef<number | null>(null);
  const popup = useRef<HTMLElement | null>(null);
  const [query, setQuery] = useState<Query | null>(null);
  const [active, setActive] = useState(0);
  const needle = query?.query.toLocaleLowerCase() ?? "";
  const suggestions = policies.filter((policy) =>
    `${policy.policyId} ${policy.title ?? ""} ${policy.domainKey}`.toLocaleLowerCase().includes(needle)).slice(0, 12);
  const activeIndex = Math.min(active, Math.max(suggestions.length - 1, 0));
  const optionId = (policyId: string) => `${id}-policy-${policyId}`;
  const known = new Set(policies.map((policy) => policy.policyId));
  // Without a loaded list nothing can be called unknown.
  const unknown = status !== "ready" ? [] : [...new Set(findPolicyMentions(value).map((mention) => mention.policyId))].filter((policyId) => !known.has(policyId));
  const emptyMessage = status === "loading" ? t("gc.policiesLoading")
    : status === "error" ? t("gc.policiesError")
      : policies.length === 0 ? t("gc.noLivePolicies") : t("gc.noPolicyMatch", { query: query?.query ?? "" });

  useLayoutEffect(() => {
    if (pendingCursor.current === null || !ref.current) return;
    ref.current.setSelectionRange(pendingCursor.current, pendingCursor.current);
    pendingCursor.current = null;
  }, [value]);

  // A tall box can put the list below the fold or under the sticky save bar: bring it into view once it opens.
  const popupKey = query ? `${query.start}:${suggestions.length > 0}` : null;
  useEffect(() => {
    if (popupKey) popup.current?.scrollIntoView?.({ block: "nearest" });
  }, [popupKey]);

  const update = (next: string, cursor: number, end = cursor) => {
    const nextQuery = cursor === end ? queryAt(next, cursor) : null;
    if (nextQuery?.start !== query?.start || nextQuery?.query !== query?.query) setActive(0);
    setQuery(nextQuery);
  };
  const choose = (policy: PolicySummary | undefined) => {
    // The caret may have moved since the query was computed: only replace a range that still matches it.
    const caret = ref.current?.selectionStart ?? -1;
    if (!query || !policy || caret < query.start || caret > query.end) return;
    const inserted = `${MENTION_PREFIX}${policy.policyId}`;
    pendingCursor.current = query.start + inserted.length;
    onChange(`${value.slice(0, query.start)}${inserted}${value.slice(query.end)}`);
    setQuery(null);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!query || suggestions.length === 0) return;
    if (event.key === "Escape") { event.preventDefault(); setQuery(null); }
    else if (event.key === "ArrowDown") { event.preventDefault(); setActive(Math.min(activeIndex + 1, suggestions.length - 1)); }
    else if (event.key === "ArrowUp") { event.preventDefault(); setActive(Math.max(activeIndex - 1, 0)); }
    else if (event.key === "Enter") { event.preventDefault(); choose(suggestions[activeIndex]); }
  };
  const listId = `${id}-policies`;
  return (
    <div className="relative">
      <Textarea
        ref={ref}
        id={id}
        role="combobox"
        aria-label={label}
        aria-autocomplete="list"
        aria-expanded={Boolean(query && suggestions.length)}
        aria-controls={listId}
        aria-activedescendant={query && suggestions[activeIndex] ? optionId(suggestions[activeIndex]!.policyId) : undefined}
        className={cn(className)}
        value={value}
        onChange={(event) => { onChange(event.target.value); update(event.target.value, event.target.selectionStart, event.target.selectionEnd); }}
        onKeyDown={onKeyDown}
        onKeyUp={(event) => {
          if (["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(event.key) && query && suggestions.length) return;
          update(event.currentTarget.value, event.currentTarget.selectionStart, event.currentTarget.selectionEnd);
        }}
        onClick={(event) => update(event.currentTarget.value, event.currentTarget.selectionStart, event.currentTarget.selectionEnd)}
        onBlur={() => setQuery(null)}
      />
      {query && suggestions.length > 0 ? (
        <ul ref={(element) => { popup.current = element; }} id={listId} role="listbox" aria-label={t("gc.policySuggestions")} className="border-border-subtle bg-surface absolute left-0 right-0 top-full z-20 mt-1 max-h-56 scroll-mb-24 overflow-auto rounded-md border shadow-md">
          {suggestions.map((policy, index) => (
            <li key={policy.policyId} id={optionId(policy.policyId)} role="option" aria-selected={index === activeIndex}
              className={`cursor-pointer px-3 py-2 text-sm ${index === activeIndex ? "bg-surface-raised" : ""}`}
              onMouseDown={(event) => event.preventDefault()} onClick={() => choose(policy)}>
              <span className="font-medium">{MENTION_PREFIX}{policy.policyId}</span>
              {policy.title ? <span className="text-muted ml-2 text-xs">{policy.title}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {/* Always mounted, so a screen reader announces the message when its text changes. */}
      <span role="status" aria-label={t("gc.policySuggestions")} className="sr-only">
        {query && suggestions.length === 0 ? emptyMessage : ""}
      </span>
      {query && suggestions.length === 0 ? (
        <div ref={(element) => { popup.current = element; }} aria-hidden="true" className="border-border-subtle bg-surface text-muted absolute left-0 right-0 top-full z-20 mt-1 scroll-mb-24 rounded-md border px-3 py-2 text-sm shadow-md">
          {emptyMessage}
        </div>
      ) : null}
      {unknown.length ? <p role="alert" className="text-danger mt-1 text-xs">{t("gc.unknownPolicies", { ids: unknown.join(", ") })}</p> : null}
    </div>
  );
}
